/** Legacy single-row id for toolsandbubbles.com — never use as a SaaS tenant default. */
export const LEGACY_TB_COMPANY_SETTINGS_ID = '00000000-0000-0000-0000-000000000001';

export type CompanySettingsDraft = {
  id: string;
  company_name: string;
  trading_name: string;
  tagline: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  phone: string;
  email: string;
  gst_number: string;
  gst_enabled: boolean;
  gst_registration_type: 'unregistered' | 'regular' | 'composition';
  gst_rate: number;
  cgst_rate: number;
  sgst_rate: number;
  igst_rate: number;
  company_state_code: string;
  default_place_of_supply_state: string;
  default_sac_code: string;
  upi_id: string;
  logo_url: string;
  timezone: string;
  currency: string;
  bank_name: string;
  account_number: string;
  account_name: string;
  branch: string;
  ifsc_code: string;
  upi_qr_url: string;
};

/**
 * Empty Company Profile form. SaaS tenants inherit locale/tax from the active org
 * and must never seed Tools & Bubbles' UUID, INR, or Kerala GST defaults.
 */
export function createEmptyCompanySettingsDraft(input: {
  isSaas: boolean;
  orgName?: string | null;
  locale?: { timezone?: string | null; currency?: string | null; country?: string | null } | null;
  tax?: { rate?: number | null } | null;
}): CompanySettingsDraft {
  const blank: CompanySettingsDraft = {
    id: '',
    company_name: String(input.orgName ?? '').trim(),
    trading_name: '',
    tagline: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: '',
    phone: '',
    email: '',
    gst_number: '',
    gst_enabled: false,
    gst_registration_type: 'unregistered',
    gst_rate: 0,
    cgst_rate: 0,
    sgst_rate: 0,
    igst_rate: 0,
    company_state_code: '',
    default_place_of_supply_state: '',
    default_sac_code: '',
    upi_id: '',
    logo_url: '',
    timezone: '',
    currency: '',
    bank_name: '',
    account_number: '',
    account_name: '',
    branch: '',
    ifsc_code: '',
    upi_qr_url: '',
  };

  if (!input.isSaas) {
    return {
      ...blank,
      id: LEGACY_TB_COMPANY_SETTINGS_ID,
      gst_rate: 18,
      cgst_rate: 9,
      sgst_rate: 9,
      igst_rate: 18,
      company_state_code: '32',
      default_place_of_supply_state: 'Kerala',
      timezone: 'Asia/Kolkata',
      currency: 'INR',
      country: 'IN',
    };
  }

  const taxRate = Number(input.tax?.rate);
  return {
    ...blank,
    timezone: String(input.locale?.timezone ?? '').trim(),
    currency: String(input.locale?.currency ?? '').trim(),
    country: String(input.locale?.country ?? '')
      .trim()
      .toUpperCase(),
    gst_rate: Number.isFinite(taxRate) ? taxRate : 0,
  };
}

/** Keep company_settings in lockstep with org locale so leftover USD/US never outrank AED. */
export function companySettingsPatchFromLocale(locale: {
  currency?: string | null;
  country?: string | null;
  timezone?: string | null;
} | null | undefined): Record<string, string> {
  const patch: Record<string, string> = {};
  const currency = String(locale?.currency ?? '').trim();
  const country = String(locale?.country ?? '')
    .trim()
    .toUpperCase();
  const timezone = String(locale?.timezone ?? '').trim();
  if (currency) patch.currency = currency;
  if (country) patch.country = country;
  if (timezone) patch.timezone = timezone;
  return patch;
}
